<template>
  <section class="space-y-4">
    <h2 class="text-base uppercase text-white/75">
      {{ $t("information") }}
    </h2>
    <BaseBoxColor variant="gray" class="w-full space-y-2 p-4">
      <GridRow>
        <GridCol :span-md="6" class="space-y-4">
          <div class="flex flex-col">
            <div class="flex items-center gap-4">
              <span class="text-lg font-semibold">{{ $t("description") }}</span>
              <button
                @click="editDescription = !editDescription"
                aria-label="edit"
                v-if="!editDescription"
              >
                <span class="material-symbols-rounded text-base">edit</span>
              </button>
            </div>
            <p v-if="!editDescription" 
            class="max-h-[400px] overflow-y-auto"
            >{{ description }}</p>
            <div v-else>
              <TextareaField
                id="description"
                name="description"
                v-model="description"
                style="margin-left: -5px; margin-top: 10px; margin-bottom: 10px;"
                :rows="Math.max(4, Math.min(Math.floor(description.length / 100), 20))"
                required
              />
              <BaseButton type="submit"
                @click="saveDescription"
                :disabled="isSubmitting">{{
                  $t("save")
                }}
              </BaseButton>
            </div>
          </div>
          <div class="flex flex-col gap-2">
            <div class="flex gap-4 items-center">
              <span class="text-lg font-semibold">{{ $t("scope") }}</span>
              <button
                @click="editScope = !editScope"
                aria-label="edit"
                v-if="!editScope"
              >
                <span class="material-symbols-rounded text-base">edit</span>
              </button>
            </div>

            <div v-if="!editScope">
              <span class="font-semibold">{{ scope.slug }}</span><span class="font-semibold" v-if="scope.description">: </span>
              <span>{{ scope.description }}</span>
            </div>
            <div v-else>
              <form class="mt-4 flex items-start gap-x-2" @submit.prevent="onSubmit">
                <div class="grow">
                  <InputText
                    list="scopeList"
                    name="scopeList"
                    class="w-full"
                    v-model="scopeData"
                    :placeholder="$t('scopeName')"
                  />
                </div>
                <datalist id="scopeList">
                  <option v-for="scope in availableScopes.map(scope => scope.slug)" :key="scope.id" :value="scope.slug">
                    {{ scope }}
                  </option>
                </datalist>
                <BaseButton
                  class="h-[2.6rem]"
                  type="submit"
                  size="xs"
                  :disabled="!availableScopes.find((scope) => scope.slug === scopeData)"  
                >
                  {{ $t("done") }}
                </BaseButton>
              </form>
            </div>
          </div>
          <h3 class="text-base uppercase text-white/75">{{ $t("type") }}</h3>
          <strong class="text-xl">{{ type }}</strong>
        </GridCol>
        <GridCol v-if="props.type == 'dynamic'" :span-md="6">
          <StandardChart v-if="chartOptions" class="h-[12.5rem] w-full" :options="chartOptions" />
        </GridCol>
      </GridRow>
    </BaseBoxColor>
  </section>
</template>

<script setup>
import { onMounted, ref, watch } from 'vue';
import { StatisticsService } from "@/api/services";
import useIntentStore from "@/stores/intent";
import TextareaField from '@/components/form-fields/TextareaField.vue';
import useOrganizationStore from "@/stores/organization";
import { storeToRefs } from "pinia";
import useScopeStore from "@/stores/scope";
import { useI18n } from "vue-i18n";

const { t } = useI18n();

const { fetchScopeList } = useScopeStore();
const { fetchOrganizationList } = useOrganizationStore();

const scopeData = ref()

const props = defineProps({
  description: String,
  type: String,
  scope: {
    type: Object,
    required: true
  },
  id: {
    type: Number,
    required: true,
  },
});

const { scopeList } = storeToRefs(useScopeStore());
const { organizationList } = storeToRefs(useOrganizationStore());

const chartOptions = ref()
const editDescription = ref(false);
const editScope = ref(false);
const description = ref(props.description);
const availableScopes = ref([]);
const scopeId = ref(props.scope.id);

const scopeSelectRef = ref(null);

function saveDescription() {
  const { updateDescription } = useIntentStore();
  updateDescription(props.id, { description: description.value }).then(() => {
    editDescription.value = false;
  });
}

const onSubmit = () => {
  const { updateScope } = useIntentStore();
  updateScope(props.id, { scope: availableScopes.value.find((scope) => scope.slug === scopeData.value) }).then(() => {
    editScope.value = false;
  });

}

onMounted(async () => {
  try{
    fetchScopeList();
    fetchOrganizationList();
    if(props.type == "dynamic"){
      let tempStats = await StatisticsService.getStatistics("intent", props.id);
      chartOptions.value = tempStats.data.jsConfig
    }
  } catch (e) {
    console.error(e)
  }
});

watch([scopeList, organizationList], () => {
  availableScopes.value = [];
  organizationList.value.forEach(org => {
    scopeList.value.forEach(scope => {
      if (scope.OrganizationId === org.id) {
        availableScopes.value.push(scope);
      }
    });
  });
});

watch(editScope, (val) => {
  if (val && scopeSelectRef.value) {
    scopeSelectRef.value.selectedValue = scopeId.value;
  }
});
</script>
